// MISMO 3.4 Compliant Loan Application Form Fields
// Based on Uniform Residential Loan Application (URLA) / Fannie Mae Form 1003

export const MISMO_SECTIONS = {
  // Section 1: Borrower Information (Multi-entry for borrower + co-borrowers)
  borrowerInfo: {
    key: 'borrowerInfo',
    title: 'Borrower Information',
    icon: '👤',
    description: 'Personal details about borrowers on this application',
    multiEntry: true,
    entryLabel: 'Borrower',
    addButtonLabel: 'Add Co-Borrower',
    minEntries: 1,
    fields: [
      { 
        id: 'borrowerType', 
        label: 'Borrower Type', 
        type: 'select', 
        required: true,
        options: ['Primary Borrower', 'Co-Borrower (Spouse)', 'Co-Borrower (Non-Spouse)'],
        mismo: 'BORROWER/BORROWER_DETAIL/BorrowerClassificationType',
      },
      { id: 'firstName', label: 'First Name', type: 'text', required: true, mismo: 'BORROWER/NAME/FirstName' },
      { id: 'middleName', label: 'Middle Name', type: 'text', required: false, mismo: 'BORROWER/NAME/MiddleName' },
      { id: 'lastName', label: 'Last Name', type: 'text', required: true, mismo: 'BORROWER/NAME/LastName' },
      { id: 'suffix', label: 'Suffix', type: 'select', options: ['', 'Jr.', 'Sr.', 'II', 'III', 'IV'], required: false, mismo: 'BORROWER/NAME/SuffixName' },
      { id: 'ssn', label: 'Social Security Number', type: 'ssn', required: true, mismo: 'BORROWER/TAXPAYER_IDENTIFIERS/TaxpayerIdentifierValue' },
      { id: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true, mismo: 'BORROWER/BORROWER_DETAIL/BorrowerBirthDate' },
      { id: 'citizenship', label: 'Citizenship Status', type: 'select', options: ['U.S. Citizen', 'Permanent Resident Alien', 'Non-Permanent Resident Alien', 'Foreign National'], required: true, mismo: 'BORROWER/BORROWER_DETAIL/CitizenshipResidencyType' },
      { id: 'maritalStatus', label: 'Marital Status', type: 'select', options: ['Married', 'Separated', 'Unmarried', 'Divorced', 'Widowed', 'Civil Union', 'Domestic Partnership'], required: true, mismo: 'BORROWER/BORROWER_DETAIL/MaritalStatusType' },
      { id: 'dependentsCount', label: 'Number of Dependents', type: 'number', required: true, mismo: 'BORROWER/DEPENDENTS/DependentCount' },
      { id: 'dependentsAges', label: 'Ages of Dependents', type: 'text', required: false, placeholder: 'e.g., 5, 8, 12', mismo: 'BORROWER/DEPENDENTS/DependentAgeYearsCount' },
      { id: 'willSignNote', label: 'Will this borrower sign the note?', type: 'boolean', required: true, showFor: ['Co-Borrower (Spouse)', 'Co-Borrower (Non-Spouse)'] },
      { id: 'willSignTitle', label: 'Will this borrower be on title?', type: 'boolean', required: true, showFor: ['Co-Borrower (Spouse)', 'Co-Borrower (Non-Spouse)'] },
    ],
    education: {
      title: 'Why We Need This Information',
      description: 'Your personal information is required by federal law to process your mortgage application. You can add co-borrowers (spouse or non-spouse) to strengthen your application.',
    },
  },

  // Section 1a: Contact Information
  contactInfo: {
    key: 'contactInfo',
    title: 'Contact Information',
    icon: '📱',
    description: 'How to reach you',
    fields: [
      { id: 'homePhone', label: 'Home Phone', type: 'phone', required: false, mismo: 'BORROWER/CONTACT_POINTS/CONTACT_POINT/ContactPointTelephoneValue' },
      { id: 'cellPhone', label: 'Cell Phone', type: 'phone', required: true, mismo: 'BORROWER/CONTACT_POINTS/CONTACT_POINT/ContactPointTelephoneValue' },
      { id: 'workPhone', label: 'Work Phone', type: 'phone', required: false, mismo: 'BORROWER/CONTACT_POINTS/CONTACT_POINT/ContactPointTelephoneValue' },
      { id: 'email', label: 'Email Address', type: 'email', required: true, mismo: 'BORROWER/CONTACT_POINTS/CONTACT_POINT/ContactPointEmailValue' },
    ],
    education: {
      title: 'Contact Preferences',
      description: 'We will use this information to send you updates about your application and important documents.',
    },
  },

  // Section 1b: Current Address
  currentAddress: {
    key: 'currentAddress',
    title: 'Current Address',
    icon: '🏠',
    description: 'Where you currently live',
    fields: [
      { id: 'currentStreet', label: 'Street Address', type: 'text', required: true, fullWidth: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/ADDRESS/AddressLineText' },
      { id: 'currentUnit', label: 'Unit/Apt #', type: 'text', required: false, mismo: 'BORROWER/RESIDENCES/RESIDENCE/ADDRESS/AddressUnitIdentifier' },
      { id: 'currentCity', label: 'City', type: 'text', required: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/ADDRESS/CityName' },
      { id: 'currentState', label: 'State', type: 'select', options: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'], required: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/ADDRESS/StateCode' },
      { id: 'currentZip', label: 'ZIP Code', type: 'text', required: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/ADDRESS/PostalCode' },
      { id: 'currentCountry', label: 'Country', type: 'select', options: ['USA', 'Other'], required: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/ADDRESS/CountryCode' },
      { id: 'housingType', label: 'Housing Type', type: 'select', options: ['Own', 'Rent', 'Living Rent Free'], required: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/BorrowerResidencyBasisType' },
      { id: 'monthlyRent', label: 'Monthly Rent/Housing Payment', type: 'currency', required: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/BorrowerResidencyDurationMonthsCount' },
      { id: 'yearsAtAddress', label: 'Years at This Address', type: 'number', required: true, mismo: 'BORROWER/RESIDENCES/RESIDENCE/BorrowerResidencyDurationYearsCount' },
      { id: 'monthsAtAddress', label: 'Months at This Address', type: 'number', required: true },
      // Subject property option (only for refinance)
      { id: 'isSubjectProperty', label: 'This is the property I am refinancing (subject property)', type: 'boolean', required: false, fullWidth: true, showForLoanPurpose: ['Refinance', 'Cash-Out Refinance'] },
      // Mailing address section
      { id: 'mailingAddressSameAsCurrent', label: 'Is your mailing address the same as your current address?', type: 'boolean', required: true, defaultValue: 'yes' },
      { id: 'mailingStreet', label: 'Mailing Street Address', type: 'text', required: true, showWhen: { field: 'mailingAddressSameAsCurrent', value: 'no' } },
      { id: 'mailingUnit', label: 'Mailing Unit/Apt #', type: 'text', required: false, showWhen: { field: 'mailingAddressSameAsCurrent', value: 'no' } },
      { id: 'mailingCity', label: 'Mailing City', type: 'text', required: true, showWhen: { field: 'mailingAddressSameAsCurrent', value: 'no' } },
      { id: 'mailingState', label: 'Mailing State', type: 'select', options: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'], required: true, showWhen: { field: 'mailingAddressSameAsCurrent', value: 'no' } },
      { id: 'mailingZip', label: 'Mailing ZIP Code', type: 'text', required: true, showWhen: { field: 'mailingAddressSameAsCurrent', value: 'no' } },
    ],
    education: {
      title: 'Residence History',
      description: 'Lenders typically require 2 years of residence history to verify stability.',
    },
  },

  // Section 1c: Previous Address (if less than 2 years at current)
  previousAddress: {
    key: 'previousAddress',
    title: 'Previous Address',
    icon: '📍',
    description: 'Your previous residence (if less than 2 years at current address)',
    conditional: true,
    conditionSection: 'currentAddress',
    conditionCheck: (currentAddressData) => {
      const years = parseInt(currentAddressData?.yearsAtAddress) || 0;
      const months = parseInt(currentAddressData?.monthsAtAddress) || 0;
      const totalMonths = (years * 12) + months;
      return totalMonths > 0 && totalMonths < 24; // Only show if they've entered time AND it's less than 2 years
    },
    fields: [
      { id: 'prevStreet', label: 'Street Address', type: 'text', required: true },
      { id: 'prevUnit', label: 'Unit/Apt #', type: 'text', required: false },
      { id: 'prevCity', label: 'City', type: 'text', required: true },
      { id: 'prevState', label: 'State', type: 'select', options: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'], required: true },
      { id: 'prevZip', label: 'ZIP Code', type: 'text', required: true },
      { id: 'prevHousingType', label: 'Housing Type', type: 'select', options: ['Own', 'Rent', 'Living Rent Free'], required: true },
      { id: 'prevYearsAtAddress', label: 'Years at This Address', type: 'number', required: true },
      { id: 'prevMonthsAtAddress', label: 'Months at This Address', type: 'number', required: true },
    ],
    education: {
      title: 'Address History',
      description: 'We need your previous address to complete the 2-year residence history requirement.',
    },
  },

  // Section 2: Property Information
  propertyInfo: {
    key: 'propertyInfo',
    title: 'Property Information',
    icon: '🏡',
    description: 'Details about the property you want to purchase',
    fields: [
      { id: 'propertyStreet', label: 'Property Street Address', type: 'text', required: true, fullWidth: true, mismo: 'COLLATERALS/COLLATERAL/SUBJECT_PROPERTY/ADDRESS/AddressLineText' },
      { id: 'propertyUnit', label: 'Unit #', type: 'text', required: false },
      { id: 'propertyCity', label: 'City', type: 'text', required: true, mismo: 'COLLATERALS/COLLATERAL/SUBJECT_PROPERTY/ADDRESS/CityName' },
      { id: 'propertyState', label: 'State', type: 'select', options: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'], required: true },
      { id: 'propertyZip', label: 'ZIP Code', type: 'text', required: true },
      { id: 'propertyCounty', label: 'County', type: 'text', required: true },
      { id: 'propertyType', label: 'Property Type', type: 'select', options: ['Single Family', 'Condominium', 'Townhouse', 'Cooperative', 'Manufactured Home', 'PUD', '2-4 Unit'], required: true, mismo: 'COLLATERALS/COLLATERAL/SUBJECT_PROPERTY/PropertyUsageType' },
      { id: 'numberOfUnits', label: 'Number of Units', type: 'select', options: ['2', '3', '4'], required: true, showWhen: { field: 'propertyType', value: '2-4 Unit' }, mismo: 'COLLATERALS/COLLATERAL/SUBJECT_PROPERTY/PropertyStructureBuiltYear' },
      { id: 'occupancy', label: 'Occupancy Type', type: 'select', options: ['Primary Residence', 'Second Home', 'Investment Property'], required: true, mismo: 'COLLATERALS/COLLATERAL/SUBJECT_PROPERTY/PropertyUsageType' },
      { id: 'yearBuilt', label: 'Year Built', type: 'number', required: false },
    ],
    education: {
      title: 'Property Details',
      description: 'Property information determines loan eligibility, rates, and required documentation.',
    },
  },

  // Section 2a: Loan Information
  loanInfo: {
    key: 'loanInfo',
    title: 'Loan Details',
    icon: '💰',
    description: 'Loan amount and terms',
    fields: [
      { id: 'loanPurpose', label: 'Loan Purpose', type: 'select', options: ['Purchase', 'Refinance', 'Cash-Out Refinance', 'Construction', 'Construction-Permanent'], required: true, mismo: 'LOAN/TERMS_OF_LOAN/LoanPurposeType' },
      // Purchase-specific fields
      { id: 'purchasePrice', label: 'Purchase Price', type: 'currency', required: true, mismo: 'COLLATERALS/COLLATERAL/SUBJECT_PROPERTY/SALES_CONTRACTS/SALES_CONTRACT/SalesContractAmount', showFor: ['Purchase', 'Construction', 'Construction-Permanent'] },
      { id: 'downPaymentAmount', label: 'Down Payment Amount', type: 'currency', required: true, showFor: ['Purchase', 'Construction', 'Construction-Permanent'] },
      { id: 'downPaymentPercent', label: 'Down Payment %', type: 'number', calculated: true, showFor: ['Purchase', 'Construction', 'Construction-Permanent'] },
      // Refinance-specific fields
      { id: 'currentPropertyValue', label: 'Current Property Value (Estimated)', type: 'currency', required: true, showFor: ['Refinance', 'Cash-Out Refinance'] },
      { id: 'currentMortgageBalance', label: 'Current Mortgage Balance', type: 'currency', required: true, showFor: ['Refinance', 'Cash-Out Refinance'] },
      { id: 'cashOutAmount', label: 'Cash-Out Amount', type: 'currency', required: true, showFor: ['Cash-Out Refinance'] },
      { id: 'cashOutPurpose', label: 'Purpose of Cash-Out', type: 'select', options: ['Debt Consolidation', 'Home Improvement', 'Investment', 'Other'], required: true, showFor: ['Cash-Out Refinance'] },
      // Common fields
      { id: 'loanAmount', label: 'Loan Amount', type: 'currency', required: true, mismo: 'LOAN/TERMS_OF_LOAN/NoteAmount' },
      { id: 'loanTerm', label: 'Loan Term (Years)', type: 'select', options: ['10', '15', '20', '25', '30'], required: true, mismo: 'LOAN/TERMS_OF_LOAN/LoanMaturityPeriodCount' },
      { id: 'amortizationType', label: 'Amortization Type', type: 'select', options: ['Fixed Rate', 'Adjustable Rate (ARM)', 'Interest Only'], required: true, mismo: 'LOAN/AMORTIZATION/AmortizationType' },
    ],
    education: {
      title: 'Understanding Your Loan',
      description: 'These details determine your monthly payment and total cost of the loan over time.',
    },
  },

  // Section 3: Employment & Income (Combined)
  employmentIncome: {
    key: 'employmentIncome',
    title: 'Employment & Income',
    icon: '💼',
    description: 'Your employment history and income sources',
    multiEntry: true,
    entryLabel: 'Employment',
    addButtonLabel: 'Add Employment',
    fields: [
      // Employment Type Selection (drives conditional fields and document conditions)
      { 
        id: 'employmentType', 
        label: 'Employment Type', 
        type: 'select', 
        required: true,
        mismo: 'BORROWER/EMPLOYERS/EMPLOYER/EMPLOYMENT/EmploymentClassificationType',
        options: [
          'W-2 Employee',
          'Self-Employed',
          'Military',
          'Independent Contractor (1099)',
          'Retired',
          'Not Employed',
        ],
      },
      // Income Types (what types of income from this employment)
      { 
        id: 'incomeTypes', 
        label: 'Income Types Received', 
        type: 'multiselect', 
        required: true,
        mismo: 'BORROWER/CURRENT_INCOME/CURRENT_INCOME_ITEM/IncomeType',
        options: [
          'Base Salary/Wages',
          'Bonus',
          'Commission',
          'Overtime',
          'Tips',
          'Military Base Pay',
          'Military Allowances (BAH, BAS)',
          'Military Combat Pay',
          'Self-Employment Income',
          '1099 Contract Income',
        ],
        showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'],
      },
      // Employment Details
      { id: 'employerName', label: 'Employer/Business Name', type: 'text', required: true, mismo: 'BORROWER/EMPLOYERS/EMPLOYER/LEGAL_ENTITY/LEGAL_ENTITY_DETAIL/FullName', showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'employerStreet', label: 'Employer Street Address', type: 'text', required: true, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'employerCity', label: 'City', type: 'text', required: true, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'employerState', label: 'State', type: 'select', options: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'], required: true, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'employerZip', label: 'ZIP Code', type: 'text', required: true, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'employerPhone', label: 'Employer Phone', type: 'phone', required: false, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'positionTitle', label: 'Position/Title', type: 'text', required: true, mismo: 'BORROWER/EMPLOYERS/EMPLOYER/EMPLOYMENT/EmploymentPositionDescription', showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'isCurrent', label: 'Is this your current employment?', type: 'boolean', required: true, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'startDate', label: 'Start Date', type: 'date', required: true, mismo: 'BORROWER/EMPLOYERS/EMPLOYER/EMPLOYMENT/EmploymentStartDate', showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'endDate', label: 'End Date (if not current)', type: 'date', required: false, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'yearsInProfession', label: 'Years in this Line of Work', type: 'number', required: true, showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      // Self-Employment specific
      { id: 'ownershipShare', label: 'Ownership Share (%)', type: 'number', required: true, showFor: ['Self-Employed'] },
      { id: 'businessType', label: 'Business Type', type: 'select', options: ['Sole Proprietorship', 'Partnership', 'S-Corporation', 'C-Corporation', 'LLC'], required: true, showFor: ['Self-Employed'] },
      // Military specific
      { id: 'militaryBranch', label: 'Branch of Service', type: 'select', options: ['Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force', 'National Guard', 'Reserves'], required: true, showFor: ['Military'] },
      { id: 'militaryStatus', label: 'Military Status', type: 'select', options: ['Active Duty', 'Reserves', 'National Guard', 'Retired', 'Discharged'], required: true, showFor: ['Military'] },
      { id: 'expectedDischargeDate', label: 'Expected Discharge Date (if applicable)', type: 'date', required: false, showFor: ['Military'] },
      // Income amounts
      { id: 'baseIncome', label: 'Base Monthly Income', type: 'currency', required: true, mismo: 'BORROWER/CURRENT_INCOME/CURRENT_INCOME_ITEM/CurrentIncomeMonthlyTotalAmount', showFor: ['W-2 Employee', 'Self-Employed', 'Military', 'Independent Contractor (1099)'] },
      { id: 'overtimeIncome', label: 'Overtime (Monthly Average)', type: 'currency', required: false, showFor: ['W-2 Employee'] },
      { id: 'bonusIncome', label: 'Bonus (Monthly Average)', type: 'currency', required: false, showFor: ['W-2 Employee'] },
      { id: 'commissionIncome', label: 'Commission (Monthly Average)', type: 'currency', required: false, showFor: ['W-2 Employee', 'Independent Contractor (1099)'] },
      { id: 'tipsIncome', label: 'Tips (Monthly Average)', type: 'currency', required: false, showFor: ['W-2 Employee'] },
      { id: 'militaryAllowances', label: 'Military Allowances (BAH, BAS, etc.)', type: 'currency', required: false, showFor: ['Military'] },
      // Retirement specific
      { id: 'retirementDate', label: 'Retirement Date', type: 'date', required: true, showFor: ['Retired'] },
      { id: 'previousEmployer', label: 'Previous Employer', type: 'text', required: false, showFor: ['Retired'] },
    ],
    education: {
      title: 'Employment & Income',
      description: 'Add all employment from the past 2 years. Include all income types received from each employer.',
    },
  },

  // Section 3a: Other Income Sources (MISMO-style multi-entry with income type selection)
  otherIncome: {
    key: 'otherIncome',
    title: 'Other Income',
    icon: '💵',
    description: 'Add each additional income source individually',
    multiEntry: true,
    entryLabel: 'Income Source',
    addButtonLabel: 'Add Income Source',
    fields: [
      {
        id: 'incomeSourceType',
        label: 'Income Source Type',
        type: 'select',
        required: true,
        mismo: 'BORROWER/CURRENT_INCOME/CURRENT_INCOME_ITEM/IncomeType',
        options: [
          'Alimony',
          'Automobile Allowance',
          'Boarder Income',
          'Capital Gains',
          'Child Support',
          'Short term/Temporary disability',
          'Long Term Disability',
          'Foster Care',
          'Housing or Parsonage',
          'Interest and Dividends',
          'Mortgage Credit Certificate',
          'Mortgage Differential Payments',
          'Notes Receivable',
          'Public Assistance',
          'Retirement (e.g., Pension, IRA)',
          'Royalty Payments',
          'Separate Maintenance',
          'Social Security',
          'Trust',
          'Rental Income',
          'Unemployment Benefits',
          'VA Compensation',
        ],
      },
      { id: 'monthlyAmount', label: 'Monthly Amount', type: 'currency', required: true, mismo: 'BORROWER/CURRENT_INCOME/CURRENT_INCOME_ITEM/CurrentIncomeMonthlyTotalAmount' },
      { id: 'incomeDescription', label: 'Description/Source Details', type: 'text', required: false },
      // Conditional fields based on income type
      { id: 'payerName', label: 'Payer Name', type: 'text', required: false, showFor: ['Alimony', 'Child Support', 'Separate Maintenance'] },
      { id: 'courtOrderNumber', label: 'Court Order/Case Number', type: 'text', required: false, showFor: ['Alimony', 'Child Support', 'Separate Maintenance'] },
      { id: 'continuanceYears', label: 'Years of Continuance Expected', type: 'number', required: false },
      { id: 'propertyAddress', label: 'Rental Property Address', type: 'text', required: false, showFor: ['Rental Income'] },
      { id: 'grossRentalIncome', label: 'Gross Monthly Rental Income', type: 'currency', required: false, showFor: ['Rental Income'] },
      { id: 'rentalExpenses', label: 'Monthly Rental Expenses', type: 'currency', required: false, showFor: ['Rental Income'] },
      { id: 'awardLetterDate', label: 'Award Letter Date', type: 'date', required: false, showFor: ['Social Security', 'VA Compensation', 'Public Assistance', 'Short term/Temporary disability', 'Long Term Disability'] },
      { id: 'trustName', label: 'Trust Name', type: 'text', required: false, showFor: ['Trust'] },
    ],
    education: {
      title: 'Additional Income',
      description: 'Add each income source separately. All income must be documented with appropriate verification. Different income types require different documentation.',
    },
  },

  // Section 5: Assets (MISMO-style multi-entry with asset type selection)
  assets: {
    key: 'assets',
    title: 'Assets & Accounts',
    icon: '🏦',
    description: 'Add each asset account individually',
    multiEntry: true,
    entryLabel: 'Asset',
    addButtonLabel: 'Add Asset',
    fields: [
      { 
        id: 'assetType', 
        label: 'Asset Type', 
        type: 'select', 
        required: true,
        mismo: 'ASSETS/ASSET/ASSET_DETAIL/AssetType',
        options: [
          'Checking',
          'Savings', 
          'Money Market',
          'Certificate of Deposit',
          'Mutual Fund',
          'Stocks',
          'Stock Options',
          'Bonds',
          'Retirement (e.g., 401k, IRA)',
          'Bridge Loan Proceeds',
          'Individual Development Account',
          'Trust Account',
          'Cash Value of Life Insurance',
          'Proceeds from Real Estate',
          'Proceeds from Sale of Non-Real Estate Asset',
        ],
      },
      { id: 'institutionName', label: 'Financial Institution Name', type: 'text', required: true, mismo: 'ASSETS/ASSET/ASSET_HOLDER/NAME/FullName' },
      { id: 'accountNumber', label: 'Account Number (last 4 digits)', type: 'text', required: false },
      { id: 'accountBalance', label: 'Account Balance / Value', type: 'currency', required: true, mismo: 'ASSETS/ASSET/ASSET_DETAIL/AssetCashOrMarketValueAmount' },
      // Conditional fields based on asset type
      { id: 'numberOfShares', label: 'Number of Shares/Units', type: 'number', required: false, showFor: ['Stocks', 'Stock Options', 'Mutual Fund', 'Bonds'] },
      { id: 'currentPrice', label: 'Current Price per Share', type: 'currency', required: false, showFor: ['Stocks', 'Stock Options'] },
      { id: 'vestingSchedule', label: 'Vesting Schedule', type: 'text', required: false, showFor: ['Stock Options'] },
      { id: 'withdrawalTerms', label: 'Terms of Withdrawal', type: 'text', required: false, showFor: ['Retirement (e.g., 401k, IRA)'] },
      { id: 'trustManager', label: 'Trust Manager/Trustee Name', type: 'text', required: false, showFor: ['Trust Account'] },
      { id: 'accessConditions', label: 'Conditions for Access to Funds', type: 'text', required: false, showFor: ['Trust Account'] },
      { id: 'surrenderValue', label: 'Cash Surrender Value', type: 'currency', required: false, showFor: ['Cash Value of Life Insurance'] },
      { id: 'saleDate', label: 'Date of Sale', type: 'date', required: false, showFor: ['Proceeds from Real Estate', 'Proceeds from Sale of Non-Real Estate Asset'] },
      { id: 'saleDescription', label: 'Description of Asset Sold', type: 'text', required: false, showFor: ['Proceeds from Sale of Non-Real Estate Asset'] },
    ],
    education: {
      title: 'Asset Verification',
      description: 'Add each asset account separately. You\'ll need 2 months of statements for bank accounts. Different asset types require different documentation.',
    },
  },

  // Section 5a: Credits Toward Transaction
  credits: {
    key: 'credits',
    title: 'Credits Toward Transaction',
    icon: '💳',
    description: 'Credits applied to your down payment or closing costs',
    multiEntry: true,
    entryLabel: 'Credit',
    addButtonLabel: 'Add Credit',
    fields: [
      {
        id: 'creditType',
        label: 'Credit Type',
        type: 'select',
        required: true,
        options: [
          'Earnest Money',
          'Employer Assistance',
          'Lot Equity',
          'Relocation Funds',
          'Rent Credit',
          'Trade Equity',
        ],
      },
      { id: 'creditAmount', label: 'Credit Amount', type: 'currency', required: true },
      { id: 'creditSource', label: 'Source/Provider', type: 'text', required: true },
      { id: 'creditDescription', label: 'Description', type: 'text', required: false },
    ],
    education: {
      title: 'Transaction Credits',
      description: 'Credits reduce the amount you need to bring to closing. Each credit type requires specific documentation.',
    },
  },

  // Section 5b: Gifts or Grants
  giftsGrants: {
    key: 'giftsGrants',
    title: 'Gifts or Grants',
    icon: '🎁',
    description: 'Gift funds or grants for down payment/closing costs',
    multiEntry: true,
    entryLabel: 'Gift/Grant',
    addButtonLabel: 'Add Gift or Grant',
    fields: [
      {
        id: 'giftSource',
        label: 'Gift/Grant Source',
        type: 'select',
        required: true,
        options: [
          'Community Nonprofit',
          'Employer',
          'Federal Agency',
          'Local Agency',
          'Relative',
          'Religious Nonprofit',
          'State Agency',
          'Unmarried Partner',
          'Lender',
        ],
      },
      { id: 'giftAmount', label: 'Gift/Grant Amount', type: 'currency', required: true },
      { id: 'donorName', label: 'Donor/Grantor Name', type: 'text', required: true },
      { id: 'donorRelationship', label: 'Relationship to Borrower', type: 'text', required: false, showFor: ['Relative', 'Unmarried Partner'] },
      { id: 'donorAddress', label: 'Donor Address', type: 'text', required: false },
      { id: 'donorPhone', label: 'Donor Phone', type: 'phone', required: false },
      { id: 'isDeposited', label: 'Have funds been deposited?', type: 'boolean', required: true },
      { id: 'depositDate', label: 'Date Deposited', type: 'date', required: false },
    ],
    education: {
      title: 'Gift Funds',
      description: 'Gift funds must be documented with a gift letter and proof of transfer. The donor must also show ability to gift.',
    },
  },

  // Section 6: Liabilities (MISMO-style multi-entry with liability type selection)
  liabilities: {
    key: 'liabilities',
    title: 'Liabilities & Debts',
    icon: '💳',
    description: 'Add each debt or liability individually',
    multiEntry: true,
    entryLabel: 'Liability',
    addButtonLabel: 'Add Liability',
    fields: [
      {
        id: 'liabilityType',
        label: 'Liability Type',
        type: 'select',
        required: true,
        mismo: 'LIABILITIES/LIABILITY/LIABILITY_DETAIL/LiabilityType',
        options: [
          'Revolving',
          'Installment',
          'Alimony',
          'Child Support',
          'Separate Maintenance',
          'Student Loan',
          'Job Related Expense',
          'Other',
        ],
      },
      { id: 'creditorName', label: 'Creditor Name', type: 'text', required: true, mismo: 'LIABILITIES/LIABILITY/LIABILITY_HOLDER/NAME/FullName' },
      { id: 'accountNumber', label: 'Account Number (last 4 digits)', type: 'text', required: false },
      { id: 'monthlyPayment', label: 'Monthly Payment', type: 'currency', required: true, mismo: 'LIABILITIES/LIABILITY/LIABILITY_DETAIL/LiabilityMonthlyPaymentAmount' },
      { id: 'unpaidBalance', label: 'Unpaid Balance', type: 'currency', required: true, mismo: 'LIABILITIES/LIABILITY/LIABILITY_DETAIL/LiabilityUnpaidBalanceAmount' },
      { id: 'monthsRemaining', label: 'Months Remaining', type: 'number', required: false },
      { id: 'toBePaidOff', label: 'To be paid off at or before closing?', type: 'boolean', required: true },
      { id: 'paidByOther', label: 'Paid by someone other than borrower?', type: 'boolean', required: false },
      { id: 'paidByOtherExplanation', label: 'If paid by other, explain', type: 'text', required: false },
    ],
    education: {
      title: 'Debt-to-Income Ratio',
      description: 'Add each debt separately. Your DTI ratio compares monthly debt payments to income. Most loans require DTI below 43-50%.',
    },
  },

  // Section 7: Real Estate Owned (Multi-entry)
  realEstateOwned: {
    key: 'realEstateOwned',
    title: 'Real Estate Owned',
    icon: '🏘️',
    description: 'Other properties you own',
    multiEntry: true,
    entryLabel: 'Property',
    fields: [
      { id: 'propertyAddress', label: 'Property Address', type: 'text', required: true },
      { id: 'propertyCity', label: 'City', type: 'text', required: true },
      { id: 'propertyState', label: 'State', type: 'select', options: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'], required: true },
      { id: 'propertyZip', label: 'ZIP Code', type: 'text', required: true },
      { id: 'propertyType', label: 'Property Type', type: 'select', options: ['Single Family', 'Condo', 'Townhouse', 'Multi-Family (2-4 units)', 'Commercial', 'Land'], required: true },
      { id: 'propertyValue', label: 'Current Market Value', type: 'currency', required: true },
      { id: 'mortgageBalance', label: 'Mortgage Balance (if any)', type: 'currency', required: false },
      { id: 'monthlyPayment', label: 'Monthly Mortgage Payment', type: 'currency', required: false },
      { id: 'rentalIncome', label: 'Monthly Rental Income (if applicable)', type: 'currency', required: false },
      { id: 'occupancyStatus', label: 'Occupancy Status', type: 'select', options: ['Primary Residence', 'Second Home', 'Investment/Rental', 'Vacant'], required: true },
      { id: 'intendToSell', label: 'Do you intend to sell this property?', type: 'boolean', required: true },
    ],
    education: {
      title: 'Property Ownership',
      description: 'Add all properties you currently own. Existing properties affect your debt-to-income ratio and may provide rental income.',
    },
  },

  // Section 8: Declarations (Section 5 of URLA)
  declarations: {
    key: 'declarations',
    title: 'Declarations',
    icon: '📝',
    description: 'Required legal disclosures',
    fields: [
      // Section 5a: About This Property and Your Money for This Loan
      { id: 'primaryResidenceIntent', label: 'Will you occupy the property as your primary residence?', type: 'boolean', required: true, mismo: 'BORROWER/DECLARATION/DECLARATION_DETAIL/IntentToOccupyType' },
      { id: 'familyRelationshipSeller', label: 'Do you have a family relationship or business affiliation with the seller of the property?', type: 'boolean', required: true },
      { id: 'borrowedDownPayment', label: 'Are you borrowing any money for this real estate transaction (e.g., down payment, closing costs) that is not disclosed on this application?', type: 'boolean', required: true },
      { id: 'applyingForOtherMortgage', label: 'Have you or will you be applying for a mortgage loan on another property before closing this loan?', type: 'boolean', required: true },
      { id: 'applyingForNewCredit', label: 'Have you or will you be applying for any new credit (e.g., credit card, car loan) before closing this loan?', type: 'boolean', required: true },
      { id: 'priorityLien', label: 'Will this property be subject to a lien that could take priority over the first mortgage lien?', type: 'boolean', required: true },
      // Section 5b: About Your Finances
      { id: 'coSignerGuarantor', label: 'Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?', type: 'boolean', required: true },
      { id: 'outstandingJudgments', label: 'Are there any outstanding judgments against you?', type: 'boolean', required: true },
      { id: 'delinquentFederalDebt', label: 'Are you currently delinquent or in default on a Federal debt?', type: 'boolean', required: true },
      { id: 'partyToLawsuit', label: 'Are you a party to a lawsuit in which you potentially have any personal financial liability?', type: 'boolean', required: true },
      { id: 'deedInLieu', label: 'Have you conveyed title to any property in lieu of foreclosure in the past 7 years?', type: 'boolean', required: true },
      { id: 'shortSale', label: 'Within the past 7 years, have you completed a pre-foreclosure sale or short sale?', type: 'boolean', required: true },
      { id: 'foreclosure', label: 'Have you had property foreclosed upon in the last 7 years?', type: 'boolean', required: true },
      { id: 'bankruptcyDeclared', label: 'Have you declared bankruptcy within the past 7 years?', type: 'boolean', required: true },
      { id: 'bankruptcyType', label: 'If yes, what type of bankruptcy?', type: 'select', options: ['Chapter 7', 'Chapter 11', 'Chapter 12', 'Chapter 13'], required: false },
    ],
    education: {
      title: 'Legal Declarations',
      description: 'These questions are required by federal law. Answer honestly - false statements may result in criminal penalties.',
    },
  },

  // Section 8a: Military Service (Section 7 of URLA)
  militaryService: {
    key: 'militaryService',
    title: 'Military Service',
    icon: '🎖️',
    description: 'Information about your military service',
    fields: [
      { id: 'currentlyServing', label: 'Are you currently serving on active duty?', type: 'boolean', required: true },
      { id: 'everServed', label: 'Have you ever served in the U.S. Armed Forces?', type: 'boolean', required: true },
      { id: 'currentlyRetiredOrDischarged', label: 'Are you currently retired, discharged, or separated from service?', type: 'boolean', required: true },
      { id: 'survivingSpouse', label: 'Are you a surviving spouse of a veteran who died while on active duty or as a result of a service-connected disability?', type: 'boolean', required: true },
      { id: 'branchOfService', label: 'Branch of Service', type: 'select', options: ['Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force', 'National Guard', 'Reserves'], required: false },
      { id: 'serviceStartDate', label: 'Service Start Date', type: 'date', required: false },
      { id: 'serviceEndDate', label: 'Service End Date (or expected)', type: 'date', required: false },
    ],
    education: {
      title: 'Military Service Benefits',
      description: 'Your military service may qualify you for VA loan benefits with no down payment and competitive rates.',
    },
  },

  // Section 9: Demographics (Optional - Government Monitoring)
  demographics: {
    key: 'demographics',
    title: 'Government Monitoring',
    icon: '📊',
    description: 'Optional demographic information for fair lending monitoring',
    optional: true,
    fields: [
      { id: 'ethnicity', label: 'Ethnicity', type: 'select', options: ['Hispanic or Latino', 'Not Hispanic or Latino', 'Prefer not to answer'], required: false },
      { id: 'race', label: 'Race', type: 'multiselect', options: ['American Indian or Alaska Native', 'Asian', 'Black or African American', 'Native Hawaiian or Pacific Islander', 'White', 'Prefer not to answer'], required: false },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Female', 'Male', 'Prefer not to answer'], required: false },
    ],
    education: {
      title: 'Fair Lending Information',
      description: 'This information is collected for government monitoring purposes to ensure fair lending practices. It will not affect your application.',
    },
  },

  // Section 10: Acknowledgment & Agreement
  acknowledgment: {
    key: 'acknowledgment',
    title: 'Acknowledgment & Agreement',
    icon: '✅',
    description: 'Review and sign your application',
    fields: [
      { id: 'certifyAccurate', label: 'I certify that all information provided is true and accurate to the best of my knowledge', type: 'boolean', required: true },
      { id: 'authorizeVerification', label: 'I authorize verification of all information provided', type: 'boolean', required: true },
      { id: 'authorizeCreditPull', label: 'I authorize the lender to obtain my credit report', type: 'boolean', required: true },
      { id: 'acknowledgeESign', label: 'I agree to use electronic signatures for this application', type: 'boolean', required: true },
      { id: 'borrowerSignature', label: 'Borrower Signature (Type Full Legal Name)', type: 'text', required: true },
      { id: 'signatureDate', label: 'Date', type: 'date', required: true },
    ],
    education: {
      title: 'Final Step',
      description: 'By signing, you certify that all information is accurate. False statements may result in criminal penalties.',
    },
  },
};

// Get all sections in order
export function getMismoSections() {
  return [
    MISMO_SECTIONS.borrowerInfo,
    MISMO_SECTIONS.contactInfo,
    MISMO_SECTIONS.currentAddress,
    MISMO_SECTIONS.previousAddress,
    MISMO_SECTIONS.propertyInfo,
    MISMO_SECTIONS.loanInfo,
    MISMO_SECTIONS.employmentIncome,
    MISMO_SECTIONS.otherIncome,
    MISMO_SECTIONS.assets,
    MISMO_SECTIONS.credits,
    MISMO_SECTIONS.giftsGrants,
    MISMO_SECTIONS.liabilities,
    MISMO_SECTIONS.realEstateOwned,
    MISMO_SECTIONS.declarations,
    MISMO_SECTIONS.militaryService,
    MISMO_SECTIONS.demographics,
    MISMO_SECTIONS.acknowledgment,
  ];
}

// Get sections for display (filtering conditional ones based on data)
export function getVisibleSections(applicationData) {
  const allSections = getMismoSections();
  return allSections.filter(section => {
    if (!section.conditional) return true;
    
    // Check conditional sections based on their conditionCheck function
    if (section.conditionSection && section.conditionCheck) {
      const conditionData = applicationData?.[section.conditionSection] || {};
      return section.conditionCheck(conditionData);
    }
    
    return true;
  });
}
